import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RecentlyProcessed = ({ recentFiles, onViewDetails, onAddToPipeline }) => {
  const getScoreColor = (score) => {
    if (score >= 80) return 'text-success';
    if (score >= 60) return 'text-accent';
    if (score >= 40) return 'text-warning';
    return 'text-error';
  };

  const getScoreBgColor = (score) => {
    if (score >= 80) return 'bg-success/10';
    if (score >= 60) return 'bg-accent/10';
    if (score >= 40) return 'bg-warning/10';
    return 'bg-error/10';
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const diff = now - new Date(timestamp);
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return new Date(timestamp)?.toLocaleDateString();
  };

  if (!recentFiles || recentFiles?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-8 text-center">
        <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="FileText" size={24} color="var(--color-muted-foreground)" />
        </div>
        <h3 className="text-lg font-medium text-foreground mb-2">No recent processing</h3>
        <p className="text-muted-foreground">Recently processed CVs will appear here</p>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg card-elevation-base">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-success/10 rounded-lg flex items-center justify-center">
              <Icon name="CheckCircle" size={18} color="var(--color-success)" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Recently Processed</h3>
              <p className="text-sm text-muted-foreground">Latest AI analysis results</p>
            </div>
          </div>
          
          <Button variant="outline" size="sm" iconName="ExternalLink">
            View All
          </Button>
        </div>
      </div>
      <div className="divide-y divide-border">
        {recentFiles?.map((file) => (
          <div key={file?.id} className="p-6 hover:bg-muted/30 transition-colors">
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-4 flex-1 min-w-0">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name="User" size={20} color="var(--color-primary)" />
                  </div>
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-3 mb-2">
                    <h4 className="font-semibold text-foreground truncate">
                      {file?.candidateName}
                    </h4>
                    <div className={`
                      px-2 py-1 rounded-full text-xs font-medium
                      ${getScoreColor(file?.aiScore)} ${getScoreBgColor(file?.aiScore)}
                    `}>
                      {file?.aiScore}/100
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4 text-sm text-muted-foreground mb-3">
                    <div className="flex items-center space-x-1">
                      <Icon name="FileText" size={14} />
                      <span className="truncate">{file?.fileName}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Icon name="Clock" size={14} />
                      <span>{formatTimeAgo(file?.processedAt)}</span>
                    </div>
                  </div>

                  {file?.position && (
                    <div className="flex items-center space-x-1 text-sm text-muted-foreground mb-3">
                      <Icon name="Briefcase" size={14} />
                      <span>Matched for: {file?.position}</span>
                    </div>
                  )}

                  {file?.keySkills && file?.keySkills?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {file?.keySkills?.slice(0, 3)?.map((skill, index) => (
                        <span 
                          key={index}
                          className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-md"
                        >
                          {skill}
                        </span>
                      ))}
                      {file?.keySkills?.length > 3 && (
                        <span className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-md">
                          +{file?.keySkills?.length - 3} more
                        </span>
                      )}
                    </div>
                  )}

                  {file?.aiInsights && (
                    <div className="bg-muted/50 rounded-lg p-3 mb-3">
                      <div className="flex items-start space-x-2">
                        <Icon name="Brain" size={16} color="var(--color-primary)" className="mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-foreground mb-1">AI Insights</p>
                          <p className="text-sm text-muted-foreground line-clamp-2">
                            {file?.aiInsights}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center space-x-2 ml-4">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Eye"
                  onClick={() => onViewDetails(file?.id)}
                >
                  View
                </Button>
                
                {file?.aiScore >= 70 && (
                  <Button
                    variant="default"
                    size="sm"
                    iconName="Plus"
                    onClick={() => onAddToPipeline(file?.id)}
                  >
                    Add to Pipeline
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RecentlyProcessed;